import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { SearchConditionCard } from "@/components/SearchConditionCard";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { v2DetailUrl } from "../api/v2";
import { useV2Detail } from "../hooks/useV2Detail";
import { type V2SearchResult } from "../api/v2Search";
import { AllMattersV2Panel } from "../components/AllMattersV2Panel";
import { V2SearchForm } from "../components/v2Search/V2SearchForm";
import {
  V2SearchResults,
  resultAddress,
} from "../components/v2Search/V2SearchResults";
import {
  isInvalidPropertyNumber,
  NUMBER_MIN_LENGTH_MESSAGE,
  v2SearchConditionsSchema,
} from "../components/v2Search/v2SearchSchema";
import { useV2Search, useV2SearchChoices } from "../hooks/useV2Search";
import {
  conditionsToParams,
  defaultV2SearchConditions,
  hasV2SearchCondition,
  paramsToConditions,
  paramsToListState,
  toV2SearchRequest,
  withListState,
  type V2SearchConditions,
  type V2SearchListState,
} from "../utils/v2SearchParams";

/** 検索結果の行から開いた v2 のパネル。依頼の詳細画面と同じ取得のしかた */
const SearchResultPanel = ({
  row,
  sideBySide,
  onSideBySideChange,
  onClose,
}: {
  row: V2SearchResult;
  sideBySide: boolean;
  onSideBySideChange: (value: boolean) => void;
  onClose: () => void;
}): JSX.Element => {
  const { panel, clear } = useV2Detail(
    v2DetailUrl(row.requestId, row.detailId)
  );
  return (
    <AllMattersV2Panel
      property={{
        prefecture: row.prefecture,
        location: resultAddress(row),
        number: row.number,
        type: row.kind,
      }}
      {...panel}
      sideBySide={sideBySide}
      onSideBySideChange={onSideBySideChange}
      onClose={() => {
        onClose();
        clear();
      }}
      actions={
        <Button
          component={RouterLink}
          to={`/allmatters/detail/${row.requestId}`}
          endIcon={<KeyboardArrowRight />}
          sx={{ minHeight: MIN_TAP_TARGET }}
        >
          依頼の詳細へ{row.requestName ? `（${row.requestName}）` : ""}
        </Button>
      }
    />
  );
};

export const AllMattersSearch: React.FC = () => {
  // 検索条件・並び・ページの正本は URL。戻る・共有で同じ検索を開ける
  const [searchParams, setSearchParams] = useSearchParams();
  const conditions = useMemo(
    () => paramsToConditions(searchParams),
    [searchParams]
  );
  const listState = useMemo(
    () => paramsToListState(searchParams),
    [searchParams]
  );
  const form = useForm<V2SearchConditions>({
    defaultValues: conditions,
    resolver: zodResolver(v2SearchConditionsSchema),
  });
  const { reset, setError } = form;
  useEffect(() => {
    reset(conditions);
    if (isInvalidPropertyNumber(conditions.number)) {
      setError("number", {
        type: "validate",
        message: NUMBER_MIN_LENGTH_MESSAGE,
      });
    }
  }, [conditions, reset, setError]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  useEffect(() => {
    if (isMobile && isInvalidPropertyNumber(conditions.number)) {
      setIsSearchModalOpen(true);
    }
  }, [conditions.number, isMobile]);
  const resultsRef = useRef<HTMLDivElement>(null);
  const refreshing = useRef(false);
  const [selected, setSelected] = useState<V2SearchResult | null>(null);
  // Lives here so the layout survives switching rows (the panel remounts).
  const [sideBySide, setSideBySide] = useState(false);

  const choices = useV2SearchChoices();
  const request = useMemo(
    () => toV2SearchRequest(conditions, listState),
    [conditions, listState]
  );
  const invalidNumber = isInvalidPropertyNumber(conditions.number);
  const { data, error, isLoading, isValidating, mutate } = useV2Search(
    invalidNumber ? null : request
  );

  const search = (values: V2SearchConditions): void => {
    // 条件が変わったら1ページ目から。並び・表示件数・最新だけの切り替えは保つ
    const nextState = { ...listState, page: 1 };
    const nextRequest = toV2SearchRequest(values, nextState);
    if (JSON.stringify(nextRequest) === JSON.stringify(request)) {
      if (!isValidating && !refreshing.current) {
        refreshing.current = true;
        mutate()
          .catch(() => undefined)
          .finally(() => {
            refreshing.current = false;
          });
      }
    } else {
      setSearchParams(withListState(conditionsToParams(values), nextState));
    }
    setIsSearchModalOpen(false);
    // The form is taller than the screen: bring the count, errors and results into view.
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const clear = (): void => {
    form.reset(defaultV2SearchConditions);
    setSearchParams(
      withListState(new URLSearchParams(), { ...listState, page: 1 })
    );
  };
  const changeListState = (next: V2SearchListState): void => {
    setSearchParams(withListState(conditionsToParams(conditions), next));
  };

  const searchForm = (
    <V2SearchForm
      // Remount on URL changes so the details toggle follows back/forward.
      key={searchParams.toString()}
      form={form}
      choices={choices}
      onSearch={search}
      onClear={clear}
    />
  );

  return (
    <PagePaper>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <ManageSearchIcon fontSize="large" />
          <PageTitle>全部事項データ化結果の検索</PageTitle>
          <Link
            component={RouterLink}
            to="/allmatters/list"
            sx={{ ml: "auto", display: "inline-flex", alignItems: "center" }}
          >
            <KeyboardArrowLeft />
            一覧に戻る
          </Link>
        </Stack>
        <Alert severity="info">
          検索できるのは、解析結果の詳細を確認できる登記だけです。以前に取得した登記も、順次検索できるようになります。
        </Alert>

        {isMobile ? (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => {
              setIsSearchModalOpen(true);
            }}
            sx={{ alignSelf: "flex-start", minHeight: MIN_TAP_TARGET }}
          >
            検索条件を指定
            {hasV2SearchCondition(conditions) ? "（指定中）" : ""}
          </Button>
        ) : (
          <SearchConditionCard>{searchForm}</SearchConditionCard>
        )}

        <SectionPaper>
          <Box ref={resultsRef} sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
            {error && (
              <Alert
                severity={error.kind === "invalid" ? "warning" : "error"}
                sx={{ mb: 2 }}
                action={
                  error.kind === "other" ? (
                    <Button
                      color="inherit"
                      onClick={() => {
                        mutate().catch(() => undefined);
                      }}
                    >
                      再試行
                    </Button>
                  ) : undefined
                }
              >
                {error.messages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </Alert>
            )}
            {!error && !hasV2SearchCondition(conditions) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                条件を指定していないため、見られる結果をすべて表示しています。
              </Typography>
            )}
            <V2SearchResults
              data={error ? undefined : data}
              loading={isLoading || isValidating}
              error={Boolean(error)}
              listState={listState}
              onListStateChange={changeListState}
              onOpen={setSelected}
              isMobile={isMobile}
            />
          </Box>
        </SectionPaper>
      </Stack>

      {isMobile && (
        <Dialog
          fullScreen
          open={isSearchModalOpen}
          onClose={() => {
            setIsSearchModalOpen(false);
          }}
          aria-labelledby="v2-search-dialog-title"
        >
          <AppBar
            position="sticky"
            color="default"
            elevation={1}
            sx={{ bgcolor: "background.paper" }}
          >
            <Toolbar>
              <Typography
                id="v2-search-dialog-title"
                variant="h6"
                color="primary"
                sx={{ flex: 1, fontSize: "1.1rem" }}
              >
                検索条件
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="検索条件を閉じる"
                onClick={() => {
                  setIsSearchModalOpen(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          {searchForm}
        </Dialog>
      )}

      {selected && (
        <SearchResultPanel
          key={selected.detailId}
          row={selected}
          sideBySide={sideBySide}
          onSideBySideChange={setSideBySide}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}
    </PagePaper>
  );
};
